import Swiper, { Autoplay, EffectCreative, Navigation } from 'swiper';
import {swiperObserver} from "../../utils/swiper.utils.js";
import {hasSimpleBarOverflow} from '../../utils/simplebar.utils.js';

const activePresetClasses = ['cl-dark', 'bg-white', 'bcl-white'];
const inactivePresetClasses = ['cl-white', 'bg-black', 'bcl-black'];
const presetClasses = [...activePresetClasses, ...inactivePresetClasses];
const testimonialQuoteDesktopLineLimit = 8;
const testimonialQuoteMobileLineLimit = 12;
const testimonialQuoteLineBuffer = 0.5;

const getTestimonialQuoteLineLimit = () => (
  window.matchMedia('(max-width: 640px)').matches
    ? testimonialQuoteMobileLineLimit
    : testimonialQuoteDesktopLineLimit
);

const updateTestimonialQuoteHeight = (quote) => {
  const lineHeight = Number.parseFloat(window.getComputedStyle(quote).lineHeight);

  if (!Number.isFinite(lineHeight)) {
    return;
  }

  const testimonialQuoteLineLimit = getTestimonialQuoteLineLimit();
  const visibleHeight = lineHeight * testimonialQuoteLineLimit;
  const overflowTolerance = lineHeight * (testimonialQuoteLineLimit + testimonialQuoteLineBuffer);
  const simpleBar = window.SimpleBar?.instances?.get(quote);

  quote.style.height = 'auto';
  quote.style.setProperty('--testimonial-quote-max-height', `${visibleHeight}px`);
  simpleBar?.recalculate();

  const contentHeight = simpleBar?.contentEl?.scrollHeight ?? quote.scrollHeight;
  const hasContentOverflow = hasSimpleBarOverflow(contentHeight, overflowTolerance);
  const height = hasContentOverflow ? visibleHeight : contentHeight;

  quote.style.height = `${height}px`;
  simpleBar?.recalculate();

  quote.dataset.testimonialScrollable = hasContentOverflow.toString();
};

const initTestimonialQuoteHeights = () => {
  const quotes = document.querySelectorAll('[data-testimonial-quote]');

  if (!quotes.length) {
    return () => {};
  }

  let resizeFrame;
  const updateQuoteHeights = () => {
    window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(() => {
      quotes.forEach(updateTestimonialQuoteHeight);
    });
  };

  updateQuoteHeights();
  window.addEventListener('resize', updateQuoteHeights, {passive: true});
  window.addEventListener('load', updateQuoteHeights, {once: true});
  document.fonts?.ready?.then(updateQuoteHeights);

  return updateQuoteHeights;
};

const updateTestimonialQuoteHeights = initTestimonialQuoteHeights();

const slidesOverflow = (swiper) => {
  if (!swiper?.el || !swiper.slides?.length) {
    return false;
  }

  const lastSlide = swiper.slides[swiper.slides.length - 1];
  const slidesWidth = lastSlide.offsetLeft + lastSlide.offsetWidth;

  return slidesWidth > swiper.el.offsetWidth;
};

const scrollSlideIntoView = (swiper, index) => {
  if (!swiper || swiper.destroyed || !slidesOverflow(swiper)) {
    return;
  }

  const slide = swiper.slides[index];

  if (!slide) {
    return;
  }

  const containerWidth = swiper.el.offsetWidth;
  const currentTranslate = swiper.translate;
  const slideLeft = slide.offsetLeft;
  const slideRight = slideLeft + slide.offsetWidth;
  const visibleLeft = -currentTranslate;
  const visibleRight = visibleLeft + containerWidth;

  if (slideLeft >= visibleLeft && slideRight <= visibleRight) {
    return;
  }

  const inset = 16;
  let newTranslate = slideLeft < visibleLeft
    ? -slideLeft + inset
    : -(slideRight - containerWidth + inset);

  newTranslate = Math.min(swiper.maxTranslate(), Math.max(swiper.minTranslate(), newTranslate));

  swiper.translateTo(newTranslate, 300);
};

const setActiveFilter = (filterButtons, activeButton) => {
  filterButtons.forEach((button) => {
    let isActive = button === activeButton;

    button.classList.toggle('is-active', isActive);
    button.classList.remove(...presetClasses);
    button.classList.add(...(isActive ? activePresetClasses : inactivePresetClasses));
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
};

const getFilteredSlides = (slides, industry) => {
  let filterIndustry = String(industry || 'all');

  if (filterIndustry === 'all') {
    return slides;
  }

  return slides.filter((slide) => {
    let slideIndustries = (slide.dataset.testimonialIndustries || '').split('|').filter(Boolean);

    return slideIndustries.includes(filterIndustry);
  });
};

const applyTestimonialsFilter = (swiper, wrapperEl, originalSlides, industry) => {
  if (!swiper || swiper.destroyed || !wrapperEl || !originalSlides.length) {
    return;
  }

  let filteredSlides = getFilteredSlides(originalSlides, industry);

  wrapperEl.innerHTML = '';
  filteredSlides.forEach((slide) => wrapperEl.appendChild(slide));

  swiper.update();
  swiper.slideTo(0, 0);
};

let testimonialsSwiperWr = document.querySelectorAll('.testimonials-swiper-wr');
testimonialsSwiperWr.forEach((el) => {
  if (el) {
    let swiperEl = el.querySelector('.swiper');
    let swiperWrapperEl = swiperEl ? swiperEl.querySelector('.swiper-wrapper') : null;
    let nextEl = el.querySelector('.swiper-button-next');
    let prevEl = el.querySelector('.swiper-button-prev');
    let sectionEl = el.closest('.testimonials-sec');
    let filterButtons = sectionEl
      ? sectionEl.querySelectorAll('[data-testimonials-industry]')
      : el.querySelectorAll('[data-testimonials-industry]');
    let filtersSwiperEl = sectionEl ? sectionEl.querySelector('.testimonials-sec__filters-swiper .swiper') : null;
    let filtersSwiper = filtersSwiperEl
      ? filtersSwiperEl.swiper || new Swiper(filtersSwiperEl, {
        observer: true,
        observeParents: true,
        spaceBetween: 10,
        slidesPerView: 'auto',
        threshold: 10,
        swipeToSlide: true,
        watchOverflow: true,
        breakpoints: {
          641: {
            spaceBetween: 12,
          },
          1023: {
            spaceBetween: 14,
          },
        },
      })
      : null;

    if (!swiperEl) {
      return;
    }

    let originalSlides = swiperWrapperEl ? Array.from(swiperWrapperEl.children) : [];

    let swiper = new Swiper(swiperEl, {
      modules: [Navigation, Autoplay, EffectCreative],
      observer: true,
      observeParents: true,
      effect: 'creative',
      spaceBetween: 16,
      slidesPerView: 1,
      speed: 560,
      threshold: 10,
      touchStartPreventDefault: false,
      creativeEffect: {
        limitProgress: 2,
        perspective: true,
        prev: {
          translate: ['-3%', 18, -260],
          rotate: [0, 0, -1.5],
          opacity: 0,
          scale: 0.84,
          origin: '20% 50%',
        },
        next: {
          translate: ['8%', 12, -120],
          rotate: [0, 0, 1.2],
          opacity: 0,
          scale: 0.94,
          origin: '80% 50%',
        },
      },
      navigation: nextEl && prevEl ? {
        nextEl,
        prevEl,
      } : undefined,
      pagination: false,
      rewind: true,
      autoplay: {
        delay: 4000,
        disableOnInteraction: false,
        pauseOnMouseEnter: window.innerWidth >= 1033,
      },
    });

    swiperObserver(swiper);
    updateTestimonialQuoteHeights();

    if (filterButtons.length) {
      let activeFilter = (sectionEl || el).querySelector('[data-testimonials-industry].is-active') || filterButtons[0];

      setActiveFilter(filterButtons, activeFilter);
      applyTestimonialsFilter(swiper, swiperWrapperEl, originalSlides, activeFilter.dataset.testimonialsIndustry);
      updateTestimonialQuoteHeights();

      filterButtons.forEach((button, index) => {
        button.addEventListener('click', () => {
          setActiveFilter(filterButtons, button);
          applyTestimonialsFilter(swiper, swiperWrapperEl, originalSlides, button.dataset.testimonialsIndustry);
          updateTestimonialQuoteHeights();
          scrollSlideIntoView(filtersSwiper, index);
        });
      });
    }
  }
});
